/**
 * InvoiceList - Displays billing history with download links
 */

import React from 'react';
import { Download, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/button';
import type { PaymentResponse } from '@neokanban/types';
import { format } from 'date-fns';

interface InvoiceListProps {
  invoices: PaymentResponse[];
  loading?: boolean;
}

export const InvoiceList: React.FC<InvoiceListProps> = ({ invoices, loading = false }) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'succeeded':
        return <Badge variant="default">Paid</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'refunded':
        return <Badge variant="outline">Refunded</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>Loading invoices...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing History</CardTitle>
          <CardDescription>No invoices found</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing History</CardTitle>
        <CardDescription>
          View and download your past invoices
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className="font-medium">
                    ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                  </p>
                  {getStatusBadge(invoice.status)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {format(new Date(invoice.createdAt), 'PPP')}
                </p>
                {invoice.failureReason && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {invoice.failureReason}
                  </p>
                )}
              </div>

              {invoice.invoiceUrl && invoice.status === 'succeeded' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(invoice.invoiceUrl, '_blank')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
